import React, { Component, ReactNode } from 'react';
import { Alert } from 'react-bootstrap/lib';
import { FinanceResponseData } from '@upstart/harmony/lib/legos/financingTypes';
import { RicStateSchemaContent } from 'hooks/services';
import { buildPlaceholderFinanceResponseData } from 'fixtures/placeholderFinanceResponseData';
import {
  FormElementType,
  RicFormSchema,
  RicMetadata,
} from '../PencilEditor/RICForm/ricFormTypes';
import { renderElement } from '../PencilEditor/RICForm/SchemaComponents/SchemaFormElementRenderer';

// Half-finished content is normal here, so one element throwing must not blank the
// rest of the preview or the editor around it.
class PreviewErrorBoundary extends Component<
  { label: string; children: ReactNode },
  { failed: boolean }
> {
  static getDerivedStateFromError() {
    return { failed: true };
  }

  constructor(props: { label: string; children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  render() {
    if (this.state.failed) {
      return (
        <Alert bsStyle="warning">
          Preview unavailable for {this.props.label}. It is most likely missing
          a field the element type requires — save to see exactly what the API
          rejects.
        </Alert>
      );
    }
    return this.props.children;
  }
}

// Inert: the preview has no deal to write back to.
const noop = () => {};

// Runs while building the boundary's own props, so it has to survive an element that
// is not an object at all — a throw here escapes the boundary it is labelling.
const labelFor = (element: FormElementType, index: number) => {
  const fields: Partial<FormElementType> =
    typeof element === 'object' && element !== null ? element : {};
  const position = `element ${index + 1}`;

  if (fields.title) {
    return `“${fields.title}”`;
  }
  return fields.type ? `${position} (${fields.type})` : position;
};

// A component rather than a call in the parent, so `renderElement` runs *inside* the
// boundary: it reads `formElement.type`, which throws before rendering anything when
// the element is not an object.
function RenderedElement({
  element,
  responseData,
  metadata,
}: {
  element: FormElementType;
  responseData: FinanceResponseData;
  metadata?: RicMetadata;
}) {
  return (
    <>
      {renderElement({
        formElement: element,
        responseData,
        fetchResponseWithUpdatedData: noop,
        ricMetadata: metadata,
      })}
    </>
  );
}

function PreviewElement({
  element,
  index,
  responseData,
  metadata,
}: {
  element: FormElementType;
  index: number;
  responseData: FinanceResponseData;
  metadata?: RicMetadata;
}) {
  return (
    // Remounted per edit, because React leaves a tripped boundary tripped.
    <PreviewErrorBoundary
      key={JSON.stringify(element)}
      label={labelFor(element, index)}
    >
      <RenderedElement
        element={element}
        responseData={responseData}
        metadata={metadata}
      />
    </PreviewErrorBoundary>
  );
}

function RicSchemaPreview({ content }: { content: RicStateSchemaContent }) {
  // The backend API's OpenAPI-generated wire type necessarily widens
  // Harmony-derived `keyof`/enum fields to plain strings; this schema is
  // otherwise identical JSON.
  const schema = content as unknown as RicFormSchema;
  const { metadata } = schema;
  // Mid-edit `elements` may be any JSON, and a non-array would throw outside the
  // boundaries below.
  const elements = Array.isArray(schema.elements) ? schema.elements : [];
  const responseData = buildPlaceholderFinanceResponseData(schema);

  return (
    // `offer-review` is load-bearing: every RIC form rule in PencilEditor.scss is
    // nested inside it, and the live page wraps the form in it for that reason.
    <div className="ric-schema-preview offer-review">
      <Alert bsStyle="info">
        Rendered against placeholder deal data sized to this schema&apos;s own
        capacities: labels, sections, layout and row counts are faithful, the
        numbers are not a real contract, and the inputs are inert.
      </Alert>
      <div className="form-container ric-schema-preview-form">
        {elements.map((element, index) => (
          <PreviewElement
            // Elements carry no identity of their own.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            element={element}
            index={index}
            responseData={responseData}
            metadata={metadata}
          />
        ))}
      </div>
    </div>
  );
}

export default RicSchemaPreview;
